/**
 * Story 30.3 (ruling O9) — can this browser receive Web Push?
 *
 *   'ios-install' an iPhone / iPad / iPod (or iPadOS reporting `MacIntel` with
 *                 touch) whose app is NOT installed to the Home Screen: iOS
 *                 delivers push only to an installed web app → "Add to Home
 *                 Screen first".
 *   'unsupported' no service worker, `PushManager` or `Notification` →
 *                 "This browser can't receive notifications".
 *   'supported'   everything else.
 *
 * The iOS check runs first: Safari in a tab lacks `PushManager`, and the
 * useful answer there is "install", not "unsupported".
 */

export type PushSupport = 'ios-install' | 'unsupported' | 'supported';

export interface PushSupportEnv {
  userAgent: string;
  platform: string;
  maxTouchPoints: number;
  /** `navigator.standalone` (iOS Safari only). */
  standalone?: boolean;
  /** `matchMedia('(display-mode: standalone)').matches`. */
  displayModeStandalone: boolean;
  hasServiceWorker: boolean;
  hasPushManager: boolean;
  hasNotification: boolean;
}

export function readPushSupportEnv(): PushSupportEnv {
  const nav = typeof navigator === 'undefined' ? undefined : navigator;
  const win = typeof window === 'undefined' ? undefined : window;
  let displayModeStandalone = false;
  try {
    displayModeStandalone = Boolean(win?.matchMedia?.('(display-mode: standalone)').matches);
  } catch {
    displayModeStandalone = false;
  }
  return {
    userAgent: nav?.userAgent ?? '',
    platform: nav?.platform ?? '',
    maxTouchPoints: nav?.maxTouchPoints ?? 0,
    standalone: (nav as (Navigator & { standalone?: boolean }) | undefined)?.standalone,
    displayModeStandalone,
    hasServiceWorker: Boolean(nav && 'serviceWorker' in nav && nav.serviceWorker),
    hasPushManager: Boolean(win && 'PushManager' in win),
    hasNotification: Boolean(win && 'Notification' in win),
  };
}

export function isIos(env: Pick<PushSupportEnv, 'userAgent' | 'platform' | 'maxTouchPoints'>): boolean {
  return (
    /iPad|iPhone|iPod/.test(env.userAgent) || (env.platform === 'MacIntel' && env.maxTouchPoints > 1)
  );
}

export function isInstalled(env: Pick<PushSupportEnv, 'standalone' | 'displayModeStandalone'>): boolean {
  return env.displayModeStandalone || env.standalone === true;
}

export function pushSupport(env: PushSupportEnv = readPushSupportEnv()): PushSupport {
  if (isIos(env) && !isInstalled(env)) return 'ios-install';
  if (!env.hasServiceWorker || !env.hasPushManager || !env.hasNotification) return 'unsupported';
  return 'supported';
}
